import { useState } from 'react';
import { useJobs, useCompletedJobs, useWeekPayment, useUnpaidWeeks, useRecordJob, useApproveJob, useDeleteCompletedJob, useMarkAllWeeksPaid } from '../../api/jobs';
import {
  getSaturdayWeekStart,
  getWeekDates,
  toISODate,
  formatPence,
  formatWeekRange,
  shortDayLabel,
  calculateBonus,
} from '../../utils/jobsWeek';
import type { CompletedJob } from '@family/shared';
import ChildJobsView from './ChildJobsView';

interface Props {
  childName: string;
  isDave: boolean;
  onBack: () => void;
}

interface ApprovalModalState {
  completedJob: CompletedJob;
}

interface PaymentModalState {
  open: true;
}

export default function ParentChildDetailView({ childName, isDave, onBack }: Props) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [viewingAsChild, setViewingAsChild] = useState(false);
  const [approvalModal, setApprovalModal] = useState<ApprovalModalState | null>(null);
  const [paymentModal, setPaymentModal] = useState<PaymentModalState | null>(null);

  const weekStart = getSaturdayWeekStart(new Date(), weekOffset);
  const weekStartStr = toISODate(weekStart);
  const isCurrentWeek = weekOffset === 0;
  const weekDates = getWeekDates(weekStart);

  const { data: jobsData, isLoading: jobsLoading } = useJobs();
  const { data: completedData, isLoading: completedLoading } = useCompletedJobs(childName, weekStartStr);
  const { data: paymentData } = useWeekPayment(childName, weekStartStr);

  const recordJob = useRecordJob();
  const approveJob = useApproveJob();
  const deleteJob = useDeleteCompletedJob();
  const markAllPaid = useMarkAllWeeksPaid();

  const { data: unpaidWeeksData } = useUnpaidWeeks(childName);

  const jobs = jobsData?.data ?? [];
  const completed = completedData?.data ?? [];
  const payment = paymentData?.data ?? null;
  const unpaidWeeks = unpaidWeeksData?.data ?? [];

  const completedByJobAndDate = new Map<string, CompletedJob>();
  for (const c of completed) {
    completedByJobAndDate.set(`${c.jobId}:${c.completedDate}`, c);
  }

  const totalApprovedPence = jobs.reduce((sum, job) => {
    const count = completed.filter(c => c.jobId === job.id && c.approved).length;
    return sum + count * job.rewardPence;
  }, 0);

  const bonusPence = calculateBonus(childName, totalApprovedPence);
  const totalWithBonus = totalApprovedPence + bonusPence;

  const isPaid = payment?.paid ?? false;
  const isPastWeek = weekOffset < 0;

  const hasUnapproved = completed.some(c => !c.approved);
  // Show the settle button whenever Dave has any payable weeks for this child —
  // the unpaidWeeks query already filters to fully-approved, unpaid weeks only.
  const canSettle = isDave && unpaidWeeks.length > 0;

  async function handleCellClick(jobId: string, date: Date) {
    if (isPaid) return;

    const dateStr = toISODate(date);
    const existing = completedByJobAndDate.get(`${jobId}:${dateStr}`);

    if (!existing) {
      await recordJob.mutateAsync({ jobId, childName, completedDate: dateStr, approved: true });
    } else if (existing.approved) {
      await deleteJob.mutateAsync({ id: existing.id, childName });
    } else {
      setApprovalModal({ completedJob: existing });
    }
  }

  async function handleApproveConfirm(approve: boolean) {
    if (!approvalModal) return;
    if (approve) {
      await approveJob.mutateAsync({ id: approvalModal.completedJob.id, approved: true, childName });
    } else {
      await deleteJob.mutateAsync({ id: approvalModal.completedJob.id, childName });
    }
    setApprovalModal(null);
  }

  async function handlePayConfirm() {
    if (!paymentModal) return;
    await markAllPaid.mutateAsync({ childName });
    setPaymentModal(null);
  }

  if (viewingAsChild) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setViewingAsChild(false)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to parent view
          </button>
          <span className="text-sm text-blue-600 font-medium bg-blue-50 px-3 py-1 rounded-full">
            Viewing as {childName}
          </span>
        </div>
        <ChildJobsView childName={childName} isParentSimulating />
      </div>
    );
  }

  if (jobsLoading || completedLoading) {
    return <div className="flex items-center justify-center h-40 text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          All children
        </button>

        <h2 className="font-semibold text-gray-900">{childName}'s Jobs</h2>

        {isDave && (
          <button
            onClick={() => setViewingAsChild(true)}
            className="text-xs text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Child's view
          </button>
        )}
      </div>

      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setWeekOffset(o => o - 1)}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
          aria-label="Previous week"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="text-center">
          <div className="font-medium text-gray-900">
            {isCurrentWeek ? 'Current week' : formatWeekRange(weekStart)}
          </div>
          {!isCurrentWeek && (
            <button
              onClick={() => setWeekOffset(0)}
              className="text-xs text-blue-600 hover:underline mt-0.5"
            >
              Back to current week
            </button>
          )}
        </div>

        <button
          onClick={() => setWeekOffset(o => o + 1)}
          disabled={isCurrentWeek}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Next week"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day/job grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              {/* Job col: narrower on mobile, wider on desktop */}
              <th className="text-left py-3 px-3 text-sm font-medium text-gray-600 w-28 sm:w-52">Job</th>
              {weekDates.map(date => {
                const dateStr = toISODate(date);
                const isToday = dateStr === toISODate(new Date());
                return (
                  <th key={dateStr} className={`py-3 px-1 text-center text-xs font-medium w-8 sm:w-10 ${isToday ? 'text-blue-600 bg-blue-50/60' : 'text-gray-500'}`}>
                    {/* Narrow initial on mobile, abbreviated name on desktop */}
                    <div className="sm:hidden">{date.toLocaleDateString('en-GB', { weekday: 'narrow' })}</div>
                    <div className="hidden sm:block">{shortDayLabel(date)}</div>
                    <div className={`font-normal ${isToday ? 'text-blue-400' : 'text-gray-400'}`}>{date.getDate()}</div>
                  </th>
                );
              })}
              {/* Reward col: hidden on mobile */}
              <th className="hidden sm:table-cell py-3 px-3 text-sm font-medium text-gray-600 text-right w-16">Reward</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job, idx) => (
              <tr key={job.id} className={`border-b border-gray-50 ${idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                {/* Job name + price subtext on mobile */}
                <td className="py-2 px-3 text-sm text-gray-900">
                  <div className="leading-tight">{job.name}</div>
                  <div className="sm:hidden text-xs text-gray-400 mt-0.5">({formatPence(job.rewardPence)})</div>
                </td>
                {weekDates.map(date => {
                  const dateStr = toISODate(date);
                  const c = completedByJobAndDate.get(`${job.id}:${dateStr}`);
                  const isFuture = date > new Date();
                  const isToday = dateStr === toISODate(new Date());

                  return (
                    <td key={dateStr} className={`py-2 px-1 text-center ${isToday ? 'bg-blue-50/60' : ''}`}>
                      <button
                        onClick={() => handleCellClick(job.id, date)}
                        disabled={isFuture || isPaid || recordJob.isPending || deleteJob.isPending}
                        className={`w-7 h-7 mx-auto rounded flex items-center justify-center transition-colors ${
                          isFuture || isPaid
                            ? 'cursor-default'
                            : 'cursor-pointer hover:opacity-80'
                        } ${
                          !c
                            ? 'bg-gray-100 border border-gray-200 hover:bg-gray-200'
                            : c.approved
                            ? 'bg-green-500'
                            : 'bg-blue-400'
                        }`}
                        title={
                          !c
                            ? `Mark ${job.name} done on ${shortDayLabel(date)}`
                            : c.approved
                            ? `Click to remove`
                            : `Needs approval — click to review`
                        }
                      >
                        {c?.approved && (
                          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        {c && !c.approved && (
                          <span className="text-white font-bold text-base leading-none">+</span>
                        )}
                      </button>
                    </td>
                  );
                })}
                <td className="hidden sm:table-cell py-2 px-3 text-sm text-gray-500 text-right">{formatPence(job.rewardPence)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer: total + payment */}
      <div className="flex items-center justify-between px-1">
        <div className="text-sm text-gray-600">
          Total earned:{' '}
          <span className="font-semibold text-gray-900">{formatPence(totalWithBonus)}</span>
          {bonusPence > 0 && (
            <div className="text-xs text-green-600 mt-0.5">Includes {formatPence(bonusPence)} bonus</div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isPaid ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Paid
            </span>
          ) : isPastWeek && totalApprovedPence > 0 ? (
            canSettle ? (
              <button
                onClick={() => setPaymentModal({ open: true })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
              >
                Not paid — mark as paid
              </button>
            ) : (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-orange-100 text-orange-700"
                title={
                  hasUnapproved
                    ? 'Cannot pay out while there are unapproved jobs'
                    : !isDave
                    ? 'Only Dave can mark as paid'
                    : ''
                }
              >
                Not paid
              </span>
            )
          ) : null}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-400 px-1">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-green-500 inline-block" /> Approved
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-blue-400 inline-block" /> Awaiting approval
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-gray-100 border border-gray-200 inline-block" /> Not done
        </span>
      </div>

      {/* Approval modal */}
      {approvalModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full space-y-4">
            <h3 className="font-semibold text-gray-900 text-lg">Approve job?</h3>
            <p className="text-gray-600 text-sm">
              {childName} recorded this job on{' '}
              <span className="font-medium">
                {new Date(approvalModal.completedJob.completedDate).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'short',
                })}
              </span>
              . Approve or reject it?
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => handleApproveConfirm(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Reject
              </button>
              <button
                onClick={() => handleApproveConfirm(true)}
                className="flex-1 py-2.5 rounded-xl bg-green-500 text-white font-medium hover:bg-green-600 transition-colors"
              >
                Approve
              </button>
            </div>
            <button
              onClick={() => setApprovalModal(null)}
              className="w-full text-center text-sm text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Payment confirmation modal */}
      {paymentModal && (() => {
        const grandTotal = unpaidWeeks.reduce((sum, week) => {
          return sum + week.earnedPence + calculateBonus(childName, week.earnedPence);
        }, 0);
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full space-y-4">
              <h3 className="font-semibold text-gray-900 text-lg">Settle all outstanding weeks?</h3>

              {/* Per-week breakdown */}
              <div className="space-y-2">
                {unpaidWeeks.map(week => {
                  const bonus = calculateBonus(childName, week.earnedPence);
                  const weekTotal = week.earnedPence + bonus;
                  const weekDate = new Date(week.weekStart + 'T00:00:00');
                  return (
                    <div key={week.weekStart} className="flex items-start justify-between text-sm">
                      <span className="text-gray-600">{formatWeekRange(weekDate)}</span>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">{formatPence(weekTotal)}</span>
                        {bonus > 0 && (
                          <div className="text-xs text-green-600">incl. {formatPence(bonus)} bonus</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Grand total */}
              <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                <span className="font-medium text-gray-700">Total to pay {childName}</span>
                <span className="font-bold text-gray-900 text-lg">{formatPence(grandTotal)}</span>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setPaymentModal(null)}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePayConfirm}
                  disabled={markAllPaid.isPending}
                  className="flex-1 py-2.5 rounded-xl bg-green-500 text-white font-medium hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  {markAllPaid.isPending ? 'Saving...' : 'Confirm payment'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
